import ProjectFormCst from "../ProjectFormCst";
import dayjs from "dayjs";
import AnimationFadeIn from "@components/AnimationFadeIn";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { Box } from "@mui/material";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useState, useMemo } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { useAddEditProject } from "../hooks/useAddEditProject";

const EditProject: React.FC = () => {
  const { t } = useTranslation(["projects-page"]);
  const { idProject, id } = useParams();
  const navigate = useNavigate();
  const [forceUpdate, setForceUpdate] = useState(0);

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getProjects}`,
    idProject,
    PagesID["projects"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetProjectAssociations,
    setFinancingPlanAssociation,
    PMProjectAssociation,
    customerAssociation,
    scopeAssociation,
    subScopeAssociation,
    costCenterAssociation,
    openAssociations,
    financingPlanAssociation,
  } = useAddEditProject(dataDetail?.dto || []);

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerEditProject } = useSWRMutation(
    `${ServicesURL.postProjects}/${idProject}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.project.messageLoading"),
        title: t("edit.outcome.project.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.project.messageLoading"),
      title: t("edit.outcome.project.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);
  const [openOutcome, setOpenOutcome] = useState(false);

  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));
    const objToSend: any = {
      code: inputForm.code || null,
      title: inputForm.title || null,
      allocated_budget: Number(inputForm.allocated_budget) || null,
      project_manager_id:
        PMProjectAssociation && PMProjectAssociation.length > 0
          ? parseInt(PMProjectAssociation[0].id)
          : null,
      cost_center_id:
        costCenterAssociation && costCenterAssociation.length > 0
          ? parseInt(costCenterAssociation[0].id)
          : null,
      description: inputForm.description || null,
      expected_start_date: !!inputForm.expected_start_date
        ? dayjs(new Date(inputForm.expected_start_date)).format("YYYY-MM-DD")
        : null,
      expected_end_date: !!inputForm.expected_end_date
        ? dayjs(new Date(inputForm.expected_end_date)).format("YYYY-MM-DD")
        : null,
      customers_ids:
        customerAssociation &&
        customerAssociation.map(({ id }: any) => parseInt(id)),
      scope_id:
        scopeAssociation && scopeAssociation.length > 0
          ? parseInt(scopeAssociation[0].id)
          : null,
      sub_scope_id:
        subScopeAssociation && subScopeAssociation.length > 0
          ? parseInt(subScopeAssociation[0].id)
          : null,
      financing_plans_ids:
        financingPlanAssociation &&
        financingPlanAssociation.map(({ id }: any) => parseInt(id)),
      funded: inputForm.funded || false,
    };

    try {
      await triggerEditProject(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.project.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (el.accessorKey === "allocated_budget") {
        return { ...el, disabled: true };
      } else {
        return { ...el };
      }
    });
  }, []);

  return (
    <AnimationFadeIn key={forceUpdate}>
      {getColumns && (
        <Box>
          <ProjectFormCst
            row={dataDetail?.dto || []}
            columns={getColumns}
            onSubmit={onSubmit}
            backButton={() => {
              navigate(-1);
            }}
            setOpenAssociation={(accessorKey, open) =>
              setOpenAssociation(accessorKey, open)
            }
            handleAssociation={(accessorKey, value) =>
              handleAssociation(accessorKey, value)
            }
            handleDelete={(accessorKey, value) =>
              handleDelete(accessorKey, value)
            }
            getValues={(accessorKey) => getValues(accessorKey)}
            association={openAssociations}
            resetProjectAssociations={resetProjectAssociations}
            scopeAssociation={scopeAssociation}
            costCenterAssociation={costCenterAssociation}
            setFinancingPlanAssociation={setFinancingPlanAssociation}
            fullWidth={true}
          />
        </Box>
      )}
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            setForceUpdate((prev) => prev + 1);
            setOutcome(initialOutcome);
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditProject;
